/**
 * @file SplashSceneView.ts
 * @role Renders the branded loading screen and exposes progress/status presentation controls.
 * @owns Full-bleed art, brand lockup, progress rail, and responsive layout.
 * @reads Validated title/layout presentation config and LayoutContext.
 * @neverDoes Load assets. Decide when loading is done. Run game flow.
 * @invariants 8, 14.
 */
import { Container, Graphics, Text } from 'pixi.js';
import type { UiConfig } from '@core/config/UiConfig';
import type { IResponsiveElement, LayoutContext } from '@core/layout/LayoutContext';
import { BackgroundView, type TextureResolver } from '@ui/components/BackgroundView';
import type { GameTitleView } from '@ui/hud/GameTitleView';
import {
  LayoutMode,
  SlotLayoutModeResolver,
  type SlotLayoutMode,
} from '@core/layout/SlotLayoutModeResolver';
import type { SlotHudRect } from '@ui/hud/SlotHudLayoutEngine';

const GOLD = 0xffdf72;
const GOLD_DARK = 0x8a5419;
const INK = 0x05070c;

export class SplashSceneView extends Container implements IResponsiveElement {
  private readonly background: BackgroundView;
  private readonly scrim = new Graphics();
  private readonly barBg = new Graphics();
  private readonly barFill = new Graphics();
  private readonly status: Text;
  private readonly percentage: Text;
  private progress01 = 0;
  private barWidth = 480;
  private barHeight = 18;
  private titleLayoutMode: SlotLayoutMode = LayoutMode.Desktop;

  constructor(
    resolveTexture: TextureResolver = () => undefined,
    private readonly backgroundAssetId = 'background_loading',
    private readonly uiConfig: UiConfig,
    private readonly titleLockup: GameTitleView,
  ) {
    super();
    this.background = new BackgroundView(resolveTexture, 0x070911);

    this.status = new Text({
      text: 'PREPARING THE CASINO',
      style: { fill: 0xd9dde8, fontFamily: 'Arial', fontSize: 17, fontWeight: '700' },
    });
    this.status.anchor.set(0.5);

    this.percentage = new Text({
      text: '0%',
      style: { fill: GOLD, fontFamily: 'Arial', fontSize: 18, fontWeight: '800' },
    });
    this.percentage.anchor.set(0.5);

    this.addChild(
      this.background,
      this.scrim,
      this.titleLockup,
      this.barBg,
      this.barFill,
      this.status,
      this.percentage,
    );
    this.redrawBar();
  }

  /** 0..1 load progress; clamped. */
  setProgress(progress01: number): void {
    this.progress01 = Math.min(1, Math.max(0, progress01));
    this.percentage.text = `${Math.round(this.progress01 * 100)}%`;
    this.redrawBar();
  }

  setStatus(text: string): void {
    this.status.text = text;
  }

  applyLayout(layout: LayoutContext): void {
    this.background.applyLayout(
      layout,
      this.backgroundAssetId,
      this.uiConfig.splash.backgroundFocalX,
      this.uiConfig.splash.backgroundFocalY,
    );
    this.scrim.clear().rect(0, 0, layout.width, layout.height).fill({ color: INK, alpha: 0.38 });

    const safe = layout.safeBounds;
    const compact = layout.widthClass === 'compact' || layout.heightClass === 'compact';
    const cx = safe.x + safe.width / 2;
    const brandY = safe.y + safe.height * (compact ? 0.36 : 0.4);
    const resolvedMode = SlotLayoutModeResolver.resolve(layout, this.uiConfig.layoutPolicy);
    // The gameplay title is absent in mobile landscape, but the loading brand must remain visible.
    // Portrait and desktop use the exact same mode-specific scale as the gameplay lockup.
    this.titleLayoutMode =
      resolvedMode === LayoutMode.MobilePortrait ? LayoutMode.MobilePortrait : LayoutMode.Desktop;
    const { width: lockupWidth, height: lockupHeight } = this.titleLayoutSize(
      safe.width,
      compact,
      resolvedMode,
    );
    this.titleLockup.layout(
      splashRect(cx, brandY, lockupWidth, lockupHeight),
      this.titleLayoutMode,
    );

    this.barWidth = Math.max(220, Math.min(520, safe.width - 48));
    this.barHeight = compact ? 14 : 18;
    const progressY = safe.y + safe.height * (compact ? 0.77 : 0.75);
    this.barBg.position.set(cx, progressY);
    this.barFill.position.set(cx, progressY);
    this.status.style.fontSize = compact ? 13 : 17;
    this.status.position.set(cx, progressY + (compact ? 35 : 45));
    this.percentage.style.fontSize = compact ? 14 : 18;
    this.percentage.position.set(cx, progressY - (compact ? 27 : 34));
    this.redrawBar();
  }

  private titleLayoutSize(
    safeWidth: number,
    compact: boolean,
    resolvedMode: SlotLayoutMode,
  ): { width: number; height: number } {
    if (resolvedMode === LayoutMode.MobilePortrait) {
      return {
        width: Math.max(1, safeWidth - this.uiConfig.layoutModes.mobilePortrait.panelInset * 2),
        height: this.uiConfig.gameTitle.portraitHeight,
      };
    }
    if (resolvedMode === LayoutMode.Desktop) {
      return {
        width: Math.max(1, safeWidth - this.uiConfig.layoutModes.desktop.panelInset * 2),
        height:
          this.uiConfig.layoutModes.desktop.topReserve + this.uiConfig.gameTitle.desktopHeight,
      };
    }
    // Gameplay intentionally hides its title in mobile landscape. Keep loading branded with the
    // desktop treatment, constrained by the compact splash frame.
    return {
      width: Math.max(1, Math.min(compact ? 360 : 520, safeWidth - 32)),
      height: compact ? 138 : 160,
    };
  }


  get backgroundReady(): boolean {
    return this.background.ready;
  }

  get debugState(): {
    progress01: number;
    barWidth: number;
    title: string;
    subtitle: string;
    titleStyleId: string;
    titleLayoutMode: SlotLayoutMode;
    titleRenderedWidth: number;
    titleRenderedHeight: number;
    status: string;
  } {
    return {
      progress01: this.progress01,
      barWidth: this.barWidth,
      ...this.titleLockup.debugText(),
      titleStyleId: this.titleLockup.debugTypography().styleId,
      titleLayoutMode: this.titleLayoutMode,
      titleRenderedWidth: this.titleLockup.debugRenderedSize().width,
      titleRenderedHeight: this.titleLockup.debugRenderedSize().height,
      status: this.status.text,
    };
  }

  private redrawBar(): void {
    const innerWidth = Math.max(0, this.barWidth - 8);
    const fillWidth = innerWidth * this.progress01;
    this.barBg
      .clear()
      .roundRect(-this.barWidth / 2, -this.barHeight / 2, this.barWidth, this.barHeight, 6)
      .fill({ color: INK, alpha: 0.9 })
      .stroke({ color: GOLD_DARK, width: 2 });
    this.barFill.clear();
    if (fillWidth <= 0) return;
    this.barFill
      .roundRect(
        -this.barWidth / 2 + 4,
        -this.barHeight / 2 + 4,
        fillWidth,
        Math.max(2, this.barHeight - 8),
        4,
      )
      .fill({ color: GOLD });
  }
}

function splashRect(x: number, y: number, width: number, height: number): SlotHudRect {
  return {
    x,
    y,
    width,
    height,
    left: x - width / 2,
    right: x + width / 2,
    top: y - height / 2,
    bottom: y + height / 2,
  };
}
